// Deploy marker: 2026-08-13 retrigger production after GitHub login connection.
import React, { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";

// ── Eager: app-shell only ────────────────────────────────────────────
// Providers, UI roots, auth gating, and feature-flag helpers wrap every
// route — lazy-loading them would just suspend the whole app for no gain.
// `NotFound` stays eager too: it's the `*` fallback and is tiny, so a
// loader flash on a 404 isn't worth it.
import { Toaster } from "@/products/emailAnalytics/components/ui/toaster";
import { Toaster as Sonner } from "@/products/emailAnalytics/components/ui/sonner";
import { TooltipProvider } from "@/products/emailAnalytics/components/ui/tooltip";
import { AuthProvider } from "./products/shared/state/auth-context";
import { TenantProductConfigProvider, useTenantProductConfigContext } from "./products/shared/state/tenant-product-config-context";
import { NavigationProvider } from "./products/emailAnalytics/state/navigation-context";
import { AlertProvider } from "@/products/emailAnalytics/state/AlertContext";
import { RBACProvider, RequireAuth, RequireAdmin, RequireSuperAdmin, RedirectIfAuthenticated, useRBAC } from "@/products/shared/utils/rbac";
import { UnauthorizedPage } from "@/products/shared/components/UnauthorizedPage";
import { isEmailChatEnabled, isPennyV3Hidden, isShowAllEmailsEnabled, canAccessIntegrationsRoutes, canRoleAccessCompliancePath } from "@/products/shared/utils/feature-access";
import { useSuperAdmin } from "@/products/shared/hooks/useSuperAdmin";
import { useHasUserProductAccess } from "@/products/shared/hooks/useUserProductAccess";
import { RequireComplianceDisclaimer } from "@/products/compliance/components/RequireComplianceDisclaimer";
import { useScrollRestoration } from "./hooks/useScrollRestoration";
import NotFound from "./products/shared/pages/NotFound";

// ── Lazy: every route target ─────────────────────────────────────────
// Each route component is code-split so Rollup emits per-product chunks
// instead of one monolithic bundle. This shrinks first-load JS for users
// and — the reason this exists — keeps the production build's peak memory
// well under the CI machine's ceiling. Pages re-exported from a product's
// `pages` barrel share that product's chunk, which is the intended grouping.

// Email Analytics (v1)
const Layout = lazy(() => import("./products/emailAnalytics/components/Layout"));
const Home = lazy(() => import("./products/emailAnalytics/pages/Home"));
const Companies = lazy(() => import("./products/emailAnalytics/pages/Companies"));
const CompanyDetails = lazy(() => import("./products/emailAnalytics/pages/CompanyDetails"));
const Alerts = lazy(() => import("./products/emailAnalytics/pages/Alerts"));
const Emails = lazy(() => import("./products/emailAnalytics/pages/Emails"));
const Thread = lazy(() => import("./products/emailAnalytics/pages/Thread"));
const Admin = lazy(() => import("@/products/emailAnalytics/pages/Admin"));
const Analytics = lazy(() => import("@/products/emailAnalytics/pages/Analytics"));
const ChatPage = lazy(() => import("@/products/emailAnalytics/pages/Chat"));

// Email Analytics (v2)
const LayoutV2 = lazy(() => import("./products/emailAnalyticsV2/components/Layout"));
const CompaniesV2 = lazy(() => import("./products/emailAnalyticsV2/pages/Companies"));
const TagsV2 = lazy(() => import("./products/emailAnalyticsV2/pages/Tags"));
const AnalyticsV2 = lazy(() => import("./products/emailAnalyticsV2/pages/Analytics"));
const ExplorerV2 = lazy(() => import("./products/emailAnalyticsV2/pages/Explorer"));
const AlertsV2 = lazy(() => import("./products/emailAnalyticsV2/pages/Alerts"));

// Shared pages
const AuthConfirm = lazy(() => import("./products/shared/pages/AuthConfirm"));
const SignIn = lazy(() => import("./products/shared/pages/SignIn"));
const SignUp = lazy(() => import("./products/shared/pages/SignUp"));
const ForgotPassword = lazy(() => import("./products/shared/pages/ForgotPassword"));
const UpdatePassword = lazy(() => import("./products/shared/pages/UpdatePassword"));
const MfaVerify = lazy(() => import("./products/shared/pages/MfaVerify"));
const AuthCallback = lazy(() => import("@/products/shared/pages/AuthCallback"));
const GoogleAuthCallback = lazy(() => import("@/products/shared/pages/GoogleAuthCallback"));
const AzureAuthCallback = lazy(() => import("@/products/shared/pages/AzureAuthCallback"));
const AzureAdminAuthCallback = lazy(() => import("@/products/shared/pages/AzureAdminAuthCallback"));
const ProductSuite = lazy(() => import("@/products/shared/pages/ProductSuite"));
const Settings = lazy(() => import("@/products/shared/pages/Settings"));
const SettingsUsers = lazy(() => import("@/products/shared/pages/SettingsUsers"));
const SettingsProfile = lazy(() => import("@/products/shared/pages/SettingsProfile"));
const SettingsTenantSwitching = lazy(() => import("@/products/shared/pages/SettingsTenantSwitching"));
const SettingsConfig = lazy(() => import("@/products/shared/pages/SettingsConfig"));
const SuperAdmin = lazy(() => import("./products/shared/pages/SuperAdmin"));
const SuperAdminTenants = lazy(() => import("./products/shared/pages/SuperAdminTenants"));
const SuperAdminCompliance = lazy(() => import("./products/shared/pages/SuperAdminCompliance"));
const SuperAdminTenantConfig = lazy(() => import("./products/shared/pages/SuperAdminTenantConfig"));
const SuperAdminConnectors = lazy(() => import("./products/shared/pages/SuperAdminConnectors"));
const SuperAdminQueryPerformance = lazy(() => import("./products/shared/pages/SuperAdminQueryPerformance"));
const PayrollMessageHistory = lazy(() => import("./products/shared/pages/PayrollMessageHistory"));
const TaggingPromptHistory = lazy(() => import("./products/shared/pages/TaggingPromptHistory"));
const Observability = lazy(() => import("./products/shared/pages/Observability"));
const ApiDocs = lazy(() => import("./products/shared/pages/ApiDocs"));

// Penny (v1)
const PennyDashboard = lazy(() => import("@/products/penny/pages/Dashboard"));
const PennyConfig = lazy(() => import("@/products/penny/pages/Config"));
const PennyConfigCompanies = lazy(() => import("@/products/penny/pages/ConfigCompanies"));
const PennyConfigGeneral = lazy(() => import("@/products/penny/pages/ConfigGeneral"));
const PennyProcess = lazy(() => import("@/products/penny/pages/Process"));
const SubmitPayrollPage = lazy(() => import("./products/penny/pages/submit"));

// Payroll Testing
const PayrollTestingDashboard = lazy(() =>
  import("@/products/payrollTesting/pages").then((m) => ({ default: m.PayrollTestingDashboard })),
);

// Integrations app store
const IntegrationsAppStore = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.IntegrationsAppStore })),
);
const IntegrationsHome = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.IntegrationsHome })),
);
const IntegrationComposer = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.IntegrationComposer })),
);
const IntegrationDetail = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.IntegrationDetail })),
);
const IntegrationsExecutions = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.IntegrationsExecutions })),
);
const IntegrationExecutionDetail = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({
    default: m.IntegrationExecutionDetail,
  })),
);
const IntegrationsConnectedAccounts = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({
    default: m.IntegrationsConnectedAccounts,
  })),
);
const IntegrationsDeveloper = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({
    default: m.IntegrationsDeveloper,
  })),
);
const IntegrationsLegacyVendorRedirect = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.LegacyVendorRedirect })),
);
const QboCallback = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.QboCallback })),
);
const OAuthCallback = lazy(() =>
  import("@/products/integrations/pages").then((m) => ({ default: m.OAuthCallback })),
);

// PennyV3
const PennyV3Dashboard = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3Dashboard })));
const PennyV3Settings = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3Settings })));
const PennyV3SettingsConnections = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsConnections })));
const PennyV3SettingsRules = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsRules })));
const PennyV3SettingsCompanies = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsCompanies })));
const PennyV3SettingsEmailVisibility = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsEmailVisibility })));
const PennyV3SettingsSchemaMappings = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsSchemaMappings })));
const PennyV3SettingsConfig = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3SettingsConfig })));
const PennyV3UsageReport = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3UsageReport })));
const AllEmails = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.AllEmails })));
const PennyV3Help = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.PennyV3Help })));
const SessionDebug = lazy(() => import("@/products/pennyV3/pages").then((m) => ({ default: m.SessionDebug })));

// Chat Engine
const SmsSettingsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.SmsSettingsPage })));
const EmbeddedSessionsHubPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.EmbeddedSessionsHubPage })));
const EmbeddedSessionDetailPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.EmbeddedSessionDetailPage })));
const AdminSessionDetailPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.AdminSessionDetailPage })));
const AdminSessionsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.AdminSessionsPage })));
const CapabilityGroupsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.CapabilityGroupsPage })));
const ChatDashboard = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.ChatDashboard })));
const CompaniesPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.CompaniesPage })));
const ConnectionsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.ConnectionsPage })));
const CompanySubmissionsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.CompanySubmissionsPage })));
const KnowledgeLibraryPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.KnowledgeLibraryPage })));
const WorkflowDetailPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.WorkflowDetailPage })));
const WorkflowsPage = lazy(() => import("@/products/chat/pages").then((m) => ({ default: m.WorkflowsPage })));

// Compliance
const ComplianceDashboard = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.ComplianceDashboard })),
);
const ComplianceEtlRunsPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.EtlRunsPage })),
);
const ComplianceEtlRunDetailPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.EtlRunDetailPage })),
);
const CompliancePropertyLabelsPage = lazy(() =>
  import("@/products/compliance/pages/PropertyLabelsPage"),
);
const ComplianceCatalogPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.CatalogPage })),
);
const ComplianceCatalogPropertyDetailPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.CatalogPropertyDetailPage })),
);
const ComplianceRegulationsPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.RegulationsPage })),
);
const ComplianceRegulationDetailPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.RegulationDetailPage })),
);
const ComplianceCompaniesPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.CompaniesPage })),
);
const ComplianceDirectoryPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.DirectoryPage })),
);
const ComplianceFmlaStartPage = lazy(() => import("@/products/compliance/pages/FmlaStartPage"));
const ComplianceFmlaPacketPage = lazy(() => import("@/products/compliance/pages/FmlaPacketPage"));
const ComplianceFmlaPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.FmlaPage })),
);
const ComplianceCompanyDerivedValuesPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({
    default: m.CompanyDerivedValuesPage,
  })),
);
const ComplianceFindingReviewsPage = lazy(() => import("@/products/compliance/pages/FindingReviewsPage"));
const ComplianceAlertsPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.AlertsPage })),
);
const ComplianceHandbooksPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.HandbooksPage })),
);
const ComplianceDocumentsPage = lazy(() => import("@/products/compliance/pages/DocumentsPage"));
const ComplianceHandbookCompanyPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.HandbookCompanyPage })),
);
const ComplianceRemediationPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.RemediationPage })),
);
const ComplianceRegistrationsPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.RegistrationsPage })),
);
const ComplianceRegistrationReviewPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.RegistrationReviewPage })),
);
const ComplianceBankPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.BankPage })),
);
const ComplianceBankCompanyDetailPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.BankCompanyDetailPage })),
);
const ComplianceBankSetupPage = lazy(() =>
  import("@/products/compliance/pages").then((m) => ({ default: m.BankSetupPage })),
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable automatic refetching on window focus to prevent unwanted refreshes
      refetchOnWindowFocus: false,
      // Disable automatic refetching when query component mounts or reconnects
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Set a reasonable stale time to reduce unnecessary refetches
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Fallback shown while a lazily-loaded route chunk is fetching.
const PageLoader: React.FC = () => (
  <div className="flex h-screen items-center justify-center text-gray-500">
    Loading...
  </div>
);

// Wrapper component that conditionally requires super admin access for PennyV3
const RequireSuperAdminWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useSuperAdmin();
  const shouldHidePennyV3 = isPennyV3Hidden();
  
  // If VITE_HIDE_PENNY2 is not set, allow all authenticated users
  if (!shouldHidePennyV3) {
    return <>{children}</>;
  }
  
  // If VITE_HIDE_PENNY2 is set, require super admin access
  return isSuperAdmin ? <>{children}</> : <UnauthorizedPage />;
};

// Wrapper component that checks if All Emails feature is enabled
const RequireAllEmailsEnabled: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isEnabled = isShowAllEmailsEnabled();
  
  if (!isEnabled) {
    return <Navigate to="/penny2/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Chat Engine: tenant product flag + per-user override; super admins always allowed
const LegacyEmbeddedSessionRedirect: React.FC = () => {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  return <Navigate to={`/chat/employee-portal/${sessionId}`} replace />;
};

const RequireChatEngineAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useSuperAdmin();
  const { isProductEnabled, isLoading } = useTenantProductConfigContext();
  const { hasAccess: hasUserChatAccess, isLoading: isUserAccessLoading } =
    useHasUserProductAccess('chat');

  if (isSuperAdmin) {
    return <>{children}</>;
  }
  if (isLoading || isUserAccessLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }
  if (isProductEnabled('chat') && hasUserChatAccess) {
    return <>{children}</>;
  }
  return <UnauthorizedPage />;
};

// Compliance: super_admin, tenant admin, and user.
// Users are limited to the live pages.
const RequireComplianceAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, loading } = useRBAC();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }
  if (canRoleAccessCompliancePath(role, pathname)) {
    return <>{children}</>;
  }
  return <UnauthorizedPage />;
};

/** Auth + role + manager disclaimer gate for every /compliance route. */
const withComplianceGuards = (page: React.ReactNode) => (
  <RequireAuth>
    <RequireComplianceAccess>
      <RequireComplianceDisclaimer>{page}</RequireComplianceDisclaimer>
    </RequireComplianceAccess>
  </RequireAuth>
);

// Integrations: super admins only (tenant flag controls suite visibility, not role access)
const RequireIntegrationsAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin, loading: isSuperAdminLoading } = useSuperAdmin();

  if (isSuperAdminLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }
  if (canAccessIntegrationsRoutes(isSuperAdmin)) {
    return <>{children}</>;
  }
  return <UnauthorizedPage />;
};

const App = () => {
  // Preserve scroll position when tab focus changes
  useScrollRestoration();
  // console.log('***** FULL PAGE LOAD *****', new Date().toISOString());

  // Debug Chatlio loading
  useEffect(() => {
    const checkChatlio = () => {
      // console.log('Checking Chatlio availability:', !!window._chatlio);
      if (window._chatlio) {
        // console.log('Chatlio methods available:', Object.keys(window._chatlio));
        
        // Add a global debug function for testing
        (window as any).testChatlio = () => {
          console.log('Testing all Chatlio methods...');
          try {
            if (window._chatlio?.show) {
              console.log('Trying show()...');
              window._chatlio.show();
            }
            if (window._chatlio?.expand) {
              console.log('Trying expand()...');
              window._chatlio.expand();
            }
            if (window._chatlio?.showOrHide) {
              console.log('Trying showOrHide()...');
              window._chatlio.showOrHide();
            }
            
            // Check DOM elements
            console.log('Chatlio DOM elements:', {
              widget: document.querySelector('#chatlio-widget'),
              container: document.querySelector('#chatlio-widget-container'),
              launcher: document.querySelector('#chatlio-widget-launcher'),
              allChatlio: document.querySelectorAll('[id*="chatlio"], [class*="chatlio"]')
            });
          } catch (e) {
            console.error('Error testing Chatlio:', e);
          }
        };
        // console.log('Added window.testChatlio() function for debugging');
      }
    };
    
    // Check immediately
    checkChatlio();
    
    // Check after a delay for late loading
    const timer = setTimeout(checkChatlio, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <chatlio-widget widgetid="ef89cbdc-67a5-49d3-7eda-72d0efe7409b"></chatlio-widget>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <TenantProductConfigProvider>
          <RBACProvider>
            <AlertProvider>
              <Router>
                <NavigationProvider>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Public routes */}
                <Route path="/signin" element={
                  <RedirectIfAuthenticated>
                    <SignIn />
                  </RedirectIfAuthenticated>
                } />
                <Route path="/signup" element={<SignUp />} />
               
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/mfa/verify" element={<MfaVerify />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/google/auth/callback" element={<GoogleAuthCallback />} />
                <Route path="/azure/auth/callback" element={<AzureAuthCallback />} />
                <Route path="/azure/adminauth/callback" element={<AzureAdminAuthCallback />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/bank-setup/:token?" element={<ComplianceBankSetupPage />} />
                
                {/* Protected routes - require authentication, allow all roles */}
                <Route path="/" element={
                  <RequireAuth>
                    <ProductSuite />
                  </RequireAuth>
                } />
                
                {/* Settings routes - accessible to all authenticated users */}
                <Route path="/settings" element={
                  <RequireAuth>
                    <Settings />
                  </RequireAuth>
                } />
                <Route path="/settings/profile" element={
                  <RequireAuth>
                    <SettingsProfile />
                  </RequireAuth>
                } />
                <Route path="/settings/users" element={
                  <RequireAdmin>
                    <SettingsUsers />
                  </RequireAdmin>
                } />
                <Route path="/settings/config" element={
                  <RequireAuth>
                    <SettingsConfig />
                  </RequireAuth>
                } />
                <Route path="/settings/tenant-switching" element={
                  <RequireSuperAdmin>
                    <SettingsTenantSwitching />
                  </RequireSuperAdmin>
                } />
                
                <Route path="/auth/confirm" element={<AuthConfirm />} />

                {/* Email Analytics routes - accessible to all authenticated users */}
                <Route path="/email-analytics" element={
                  <RequireAuth>
                    <Navigate to="/email-analytics/dashboard" replace />
                  </RequireAuth>
                } />
                
                <Route path="/email-analytics/dashboard" element={
                  <RequireAuth>
                    <Layout>
                      <Home />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/companies" element={
                  <RequireAuth>
                    <Layout>
                      <Companies />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/company/:id" element={
                  <RequireAuth>
                    <Layout>
                      <CompanyDetails />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/thread/:threadId" element={
                  <RequireAuth>
                    <Layout>
                      <Thread />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/alerts" element={
                  <RequireAuth>
                    <Layout>
                      <Alerts />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/alerts/:tab" element={
                  <RequireAuth>
                    <Layout>
                      <Alerts />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/emails" element={
                  <RequireAuth>
                    <Layout>
                      <Emails />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/emails/:keywordId" element={
                  <RequireAuth>
                    <Layout>
                      <Emails />
                    </Layout>
                  </RequireAuth>
                } />
                <Route path="/email-analytics/emails/cluster/:clusterId" element={
                  <RequireAuth>
                    <Layout>
                      <Emails />
                    </Layout>
                  </RequireAuth>
                } />

                <Route path="/email-analytics/admin" element={
                  <RequireAdmin>
                    <Layout>
                      <Admin />
                    </Layout>
                  </RequireAdmin>
                } />
                <Route path="/email-analytics/analytics" element={
                  <RequireAuth>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </RequireAuth>
                } />
                {/* Email Analytics Chat (env-gated) */}
                {isEmailChatEnabled() && (
                  <Route path="/email-analytics/chat" element={
                    <RequireAuth>
                      <Layout>
                        <ChatPage />
                      </Layout>
                    </RequireAuth>
                  } />
                )}
                
                {/* Email Analytics v2 routes */}
                <Route path="/email-analytics-v2" element={
                  <RequireAuth>
                    <Navigate to="/email-analytics-v2/chat" replace />
                  </RequireAuth>
                } />
                <Route path="/email-analytics-v2/companies" element={
                  <RequireAuth>
                    <LayoutV2>
                      <CompaniesV2 />
                    </LayoutV2>
                  </RequireAuth>
                } />
                {/* Threads removed */}
                <Route path="/email-analytics-v2/tags" element={
                  <RequireAuth>
                    <LayoutV2>
                      <TagsV2 />
                    </LayoutV2>
                  </RequireAuth>
                } />
                <Route path="/email-analytics-v2/alerts" element={
                  <RequireAuth>
                    <LayoutV2>
                      <AlertsV2 />
                    </LayoutV2>
                  </RequireAuth>
                } />
                <Route path="/email-analytics-v2/analytics" element={
                  <RequireAuth>
                    <LayoutV2>
                      <AnalyticsV2 />
                    </LayoutV2>
                  </RequireAuth>
                } />
                <Route path="/email-analytics-v2/explorer" element={
                  <RequireAuth>
                    <LayoutV2>
                      <ExplorerV2 />
                    </LayoutV2>
                  </RequireAuth>
                } />
                <Route path="/email-analytics-v2/chat" element={
                  <RequireAuth>
                    <LayoutV2>
                      <div className="h-screen">
                        <ChatPage />
                      </div>
                    </LayoutV2>
                  </RequireAuth>
                } />


                
                {/* Penny routes - accessible to all authenticated users */}
                <Route path="/penny/dashboard" element={
                  <RequireAuth>
                    <PennyDashboard />
                  </RequireAuth>
                } />
                <Route path="/penny/config" element={
                  <RequireAuth>
                    <PennyConfig />
                  </RequireAuth>
                } />
                <Route path="/penny/config/companies" element={
                  <RequireAuth>
                    <PennyConfigCompanies />
                  </RequireAuth>
                } />
                <Route path="/penny/config/general" element={
                  <RequireAuth>
                    <PennyConfigGeneral />
                  </RequireAuth>
                } />
                <Route path="/penny/process" element={
                  <RequireAuth>
                    <PennyProcess />
                  </RequireAuth>
                } />
                <Route path="/penny/submit" element={
                  <RequireAuth>
                    <SubmitPayrollPage />
                  </RequireAuth>
                } />
                
                {/* Payroll Testing routes - accessible to all authenticated users */}
                <Route path="/pennyv2" element={
                  <RequireAuth>
                    <PayrollTestingDashboard />
                  </RequireAuth>
                } />
                
                {/* HCM integrations: tenant product flag + super admin only.
                    Provider callbacks stay public — the vendor redirects back
                    before the app can re-establish a session. */}
                <Route path="/integrations/qbo/callback" element={<QboCallback />} />
                <Route path="/integrations/oauth/callback" element={<OAuthCallback />} />
                <Route path="/integrations" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsHome />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/syncs" element={<Navigate to="/integrations" replace />} />
                <Route path="/integrations/new" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationComposer />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/detail/:integrationId" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationDetail />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/executions" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsExecutions />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/executions/:runId" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationExecutionDetail />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/accounts" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsConnectedAccounts />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/developer" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsDeveloper />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/catalog" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsAppStore />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/catalog/:vendorId" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsAppStore />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />
                <Route path="/integrations/build" element={<Navigate to="/integrations/new" replace />} />
                <Route path="/integrations/admin" element={<Navigate to="/integrations/developer" replace />} />
                <Route path="/integrations/:vendorId" element={
                  <RequireAuth>
                    <RequireIntegrationsAccess>
                      <IntegrationsLegacyVendorRedirect />
                    </RequireIntegrationsAccess>
                  </RequireAuth>
                } />

                {/* Chat Engine route (env-gated, but super admins can still access via direct URL) */}
                <Route path="/chat" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <ChatDashboard />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/capabilities" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <CapabilityGroupsPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/submissions" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <CompanySubmissionsPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/library" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <KnowledgeLibraryPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/workflows" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <WorkflowsPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/workflows/:id" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <WorkflowDetailPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/companies" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <CompaniesPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/connections" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <ConnectionsPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/settings/sms" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <SmsSettingsPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                {/* Chat admin (super_admin only): cross-tenant session triage. */}
                <Route path="/chat/admin/sessions" element={
                  <RequireAuth>
                    <RequireSuperAdmin>
                      <AdminSessionsPage />
                    </RequireSuperAdmin>
                  </RequireAuth>
                } />
                <Route path="/chat/admin/sessions/:sessionId" element={
                  <RequireAuth>
                    <RequireSuperAdmin>
                      <AdminSessionDetailPage />
                    </RequireSuperAdmin>
                  </RequireAuth>
                } />
                <Route path="/chat/employee-portal" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <EmbeddedSessionsHubPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/employee-portal/:sessionId" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <EmbeddedSessionDetailPage />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/embedded" element={<Navigate to="/chat/employee-portal" replace />} />
                <Route path="/chat/embedded/:sessionId" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <LegacyEmbeddedSessionRedirect />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />
                <Route path="/chat/:sessionId" element={
                  <RequireAuth>
                    <RequireChatEngineAccess>
                      <ChatDashboard />
                    </RequireChatEngineAccess>
                  </RequireAuth>
                } />

                {/* Compliance: role gate + manager disclaimer acceptance */}
                <Route path="/compliance" element={withComplianceGuards(<ComplianceDashboard />)} />
                <Route path="/compliance/etl-runs" element={withComplianceGuards(<ComplianceEtlRunsPage />)} />
                <Route path="/compliance/etl-runs/:runNumber" element={withComplianceGuards(<ComplianceEtlRunDetailPage />)} />
                <Route path="/compliance/property-labels" element={withComplianceGuards(<RequireAdmin><CompliancePropertyLabelsPage /></RequireAdmin>)} />
                <Route path="/compliance/catalog" element={withComplianceGuards(<ComplianceCatalogPage />)} />
                <Route path="/compliance/catalog/properties/:propertyId" element={withComplianceGuards(<ComplianceCatalogPropertyDetailPage />)} />
                <Route path="/compliance/regulations" element={withComplianceGuards(<ComplianceRegulationsPage />)} />
                <Route path="/compliance/regulations/:regulationId" element={withComplianceGuards(<ComplianceRegulationDetailPage />)} />
                <Route path="/compliance/companies" element={withComplianceGuards(<ComplianceCompaniesPage />)} />
                <Route path="/compliance/fmla/new" element={withComplianceGuards(<ComplianceFmlaStartPage />)} />
                <Route path="/compliance/fmla/:companyId/:requestId" element={withComplianceGuards(<ComplianceFmlaPacketPage />)} />
                <Route path="/compliance/fmla" element={withComplianceGuards(<ComplianceFmlaPage />)} />
                <Route path="/compliance/companies/:companyId" element={withComplianceGuards(<ComplianceCompanyDerivedValuesPage />)} />
                <Route path="/compliance/handbooks" element={withComplianceGuards(<ComplianceHandbooksPage />)} />
                <Route path="/compliance/documents" element={withComplianceGuards(<ComplianceDocumentsPage />)} />
                <Route path="/compliance/handbooks/:companyId" element={withComplianceGuards(<ComplianceHandbookCompanyPage />)} />
                <Route path="/compliance/finding-reviews" element={withComplianceGuards(<RequireSuperAdmin><ComplianceFindingReviewsPage /></RequireSuperAdmin>)} />
                <Route path="/compliance/alerts" element={withComplianceGuards(<ComplianceAlertsPage />)} />
                <Route path="/compliance/directory" element={withComplianceGuards(<ComplianceDirectoryPage />)} />
                <Route path="/compliance/remediation" element={withComplianceGuards(<ComplianceRemediationPage />)} />
                <Route path="/compliance/registrations" element={withComplianceGuards(<ComplianceRegistrationsPage />)} />
                <Route path="/compliance/registrations/review" element={withComplianceGuards(<ComplianceRegistrationReviewPage />)} />
                <Route path="/compliance/bank" element={withComplianceGuards(<ComplianceBankPage />)} />
                <Route path="/compliance/bank/:companyId" element={withComplianceGuards(<ComplianceBankCompanyDetailPage />)} />
                <Route path="/compliance/notifications" element={withComplianceGuards(<Navigate to="/compliance/alerts" replace />)} />

                {/* Penny 2 routes - accessible to super admin users when VITE_HIDE_PENNY2 is enabled */}
                <Route path="/penny2" element={
                  <RequireAuth>
                    <RequireSuperAdminWrapper>
                      <Navigate to="/penny2/dashboard" replace />
                    </RequireSuperAdminWrapper>
                  </RequireAuth>
                } />
          <Route path="/penny2/dashboard" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3Dashboard />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/dashboard/:threadId" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3Dashboard />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/dashboard/:threadId/:sessionId" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3Dashboard />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3Settings />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/connections" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3SettingsConnections />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/rules" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3SettingsRules />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/companies" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3SettingsCompanies />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/schema-mappings" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3SettingsSchemaMappings />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/email-visibility" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3SettingsEmailVisibility />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/config" element={
            <RequireAuth>
              <RequireAdmin>
                <PennyV3SettingsConfig />
              </RequireAdmin>
            </RequireAuth>
          } />
          <Route path="/penny2/settings/usage-report" element={
            <RequireAuth>
              <RequireAdmin>
                <PennyV3UsageReport />
              </RequireAdmin>
            </RequireAuth>
          } />
          <Route path="/penny2/all-emails" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <RequireAllEmailsEnabled>
                  <AllEmails />
                </RequireAllEmailsEnabled>
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/help" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <PennyV3Help />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
          <Route path="/penny2/debug/:sessionId" element={
            <RequireAuth>
              <RequireSuperAdminWrapper>
                <SessionDebug />
              </RequireSuperAdminWrapper>
            </RequireAuth>
          } />
                
                {/* Super Admin routes - accessible to super admin users */}
                <Route path="/super-admin" element={
                  <RequireSuperAdmin>
                    <SuperAdmin />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/tenants" element={
                  <RequireSuperAdmin>
                    <SuperAdminTenants />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/compliance" element={
                  <RequireSuperAdmin>
                    <SuperAdminCompliance />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/tenant-config" element={
                  <RequireSuperAdmin>
                    <SuperAdminTenantConfig />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/connectors" element={
                  <RequireSuperAdmin>
                    <SuperAdminConnectors />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/payroll-message-history" element={
                  <RequireSuperAdmin>
                    <PayrollMessageHistory />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/tagging-prompt-history" element={
                  <RequireSuperAdmin>
                    <TaggingPromptHistory />
                  </RequireSuperAdmin>
                } />
                <Route path="/super-admin/query-performance" element={
                  <RequireSuperAdmin>
                    <SuperAdminQueryPerformance />
                  </RequireSuperAdmin>
                } />
                <Route path="/observability" element={
                  <RequireSuperAdmin>
                    <Observability />
                  </RequireSuperAdmin>
                } />
                <Route path="/observability/sessions/:sessionId" element={
                  <RequireSuperAdmin>
                    <Observability />
                  </RequireSuperAdmin>
                } />
                <Route path="/observability/:tab" element={
                  <RequireSuperAdmin>
                    <Observability />
                  </RequireSuperAdmin>
                } />
                
                {/* Fallback routes */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </NavigationProvider>
          </Router>
        </AlertProvider>
        </RBACProvider>
        </TenantProductConfigProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
